import {
  Badge,
  Box,
  Button,
  EmptyState,
  FlexBox,
  InputText,
  Skeleton,
  Text,
} from '@dixa/lib-shared-habla';
import { useRef, useState } from 'react';

import { useInfiniteScroll } from 'hooks/useInfiniteScroll';

import { useSuggestions } from '../hooks/useSuggestions';
import { useSuggestionsFilters } from '../hooks/useSuggestionsFilters';
import { useSuggestionsSearch } from '../hooks/useSuggestionsSearch';
import { Suggestion } from '../utils/toSuggestion';

import { SuggestionRow } from './suggestionRow';
import { SuggestionsFilters } from './suggestionsFilters';
import s from './suggestionsList.module.scss';

type SuggestionsListProps = {
  selectedId: string | undefined;
  onSelect: (suggestion: Suggestion) => void;
};

export function SuggestionsList({
  selectedId,
  onSelect,
}: SuggestionsListProps): JSX.Element {
  const { instantSearchValue, searchQuery, onChange } = useSuggestionsSearch();
  const filters = useSuggestionsFilters();
  const [showFilters, setShowFilters] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { suggestions, loading, loadingMore, hasMore, total, fetchMore } =
    useSuggestions({ searchQuery, filters: filters.filters });
  const lastRowRef = useRef<HTMLLIElement>(null);

  useInfiniteScroll({
    lastRowRef,
    containerRef: scrollRef,
    fetchMore,
    hasMore,
    rowCount: suggestions.length,
  });

  const isEmpty = !loading && suggestions.length === 0;

  return (
    <FlexBox
      flexDirection="column"
      width="100%"
      height="100%"
      background="neutral.000"
      className={s.pane}
    >
      <FlexBox alignItems="center" gap="200" padding="400">
        <Text.Heading variant="heading-3" as="h1">
          Article Suggestions
        </Text.Heading>
        {total !== undefined && (
          <Badge size="small" variant="secondary">
            {total}
          </Badge>
        )}
      </FlexBox>

      <Box padding="0 400 300">
        {/* habla's `SearchInput` collapses to an icon until clicked; this needs
            a field that stays open, so it composes `InputText`. */}
        <InputText
          icon="search"
          placeholder="Search suggestions"
          aria-label="Search suggestions"
          value={instantSearchValue}
          onChange={(changeEvent) => onChange(changeEvent.target.value)}
          // A bare element is read as a possible addon descriptor and dropped,
          // so the clear button goes through the `button` addon shape.
          suffix={
            instantSearchValue
              ? {
                  type: 'button' as const,
                  button: {
                    variant: 'tertiary' as const,
                    icon: 'close' as const,
                    'aria-label': 'Clear search',
                    onClick: () => onChange(''),
                  },
                }
              : undefined
          }
        />
      </Box>

      <FlexBox flexDirection="column" gap="200" padding="0 400 300">
        <FlexBox alignItems="center" justifyContent="space-between">
          <Button
            variant="tertiary"
            icon="filter"
            aria-expanded={showFilters}
            onClick={() => setShowFilters((shown) => !shown)}
          >
            {filters.activeCount
              ? `Filters (${filters.activeCount})`
              : 'Filters'}
          </Button>
          {filters.activeCount > 0 && (
            <Button variant="tertiary" onClick={filters.clear}>
              Clear filters
            </Button>
          )}
        </FlexBox>
        {showFilters && <SuggestionsFilters filters={filters} />}
      </FlexBox>

      {/* A plain element rather than a `Box`: the infinite scroll needs a real
          DOM ref, and `Box` types its ref as the element's props. */}
      <div className={s.scroll} ref={scrollRef}>
        {isEmpty ? (
          <EmptyState
            size="small"
            icon="ai"
            title={
              filters.activeCount
                ? 'No suggestions match these filters'
                : searchQuery
                  ? 'No suggestions match your search'
                  : 'No suggestions yet'
            }
            description={
              filters.activeCount || searchQuery
                ? undefined
                : 'Drafts written by AI from your conversations show up here.'
            }
            buttonProps={
              filters.activeCount
                ? { children: 'Clear filters', onClick: filters.clear }
                : undefined
            }
          />
        ) : (
          <Box
            as="ul"
            margin="0"
            padding="0"
            className={s.rows}
            aria-label="Article suggestions"
          >
            {loading
              ? Array.from({ length: 3 }, (_, i) => (
                  <FlexBox
                    as="li"
                    key={i}
                    flexDirection="column"
                    gap="200"
                    padding="300 400"
                  >
                    <Skeleton height="14px" width="70%" />
                    <Skeleton height="14px" width="90%" />
                    <Skeleton height="14px" width="40%" />
                  </FlexBox>
                ))
              : suggestions.map((suggestion, index) => (
                  <SuggestionRow
                    key={suggestion.id}
                    ref={
                      index === suggestions.length - 1 ? lastRowRef : undefined
                    }
                    suggestion={suggestion}
                    isSelected={suggestion.id === selectedId}
                    onSelect={onSelect}
                  />
                ))}
            {loadingMore && (
              <FlexBox as="li" flexDirection="column" padding="300 400">
                <Skeleton height="14px" width="70%" />
              </FlexBox>
            )}
          </Box>
        )}
      </div>
    </FlexBox>
  );
}
